import { updateGroupPermission } from '@app/modules/admin/api/permissions';
import { StatusSwitch } from '@app/modules/admin/components/permission/StatusSwitch';
import { StationLabel } from '@app/modules/admin/screens/stations/list/StationLabel';
import { GroupPermissionForStationState, GroupPermissionSource } from '@app/modules/admin/types';
import { Group } from '@mi-front/core';
import { useToast } from '@app/core/notifications';
import { Box, Card, CardContent } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { reportError } from '../../../../core/integrations/bugsnag';

interface Props {
  readonly permissionState: GroupPermissionForStationState;
  readonly group: Group;
  readonly onUpdate?: (updatedState: GroupPermissionForStationState) => void;
}

export function GroupStationPermissionItem(p: Props) {
  const { permissionState: initialPermissionState, group, onUpdate } = p;
  const [permissionState, setPermissionState] = useState(initialPermissionState);
  const [isUpdating, setIsUpdating] = useState(false);
  const { $t } = useIntl();
  const { notifyError } = useToast();

  const hasPermission = permissionState.permission !== null;

  const handleToggle = async (enabled: boolean) => {
    if (isUpdating) {
      return;
    }

    setIsUpdating(true);
    try {
      await updateGroupPermission(permissionState.station.id, {
        groupId: group.id,
        allowed: enabled,
      });

      const updatedState: GroupPermissionForStationState = {
        ...permissionState,
        permission: enabled
          ? {
              type: GroupPermissionSource.DIRECT,
            }
          : null,
      };

      setPermissionState(updatedState);
      onUpdate?.(updatedState);
    } catch (err) {
      reportError(err as Error);
      notifyError(
        $t({
          id: 'admin.groups.viewScreen.permissions.updateError',
          defaultMessage: 'No se pudo actualizar el permiso',
        }),
      );
      // On error, revert to original state
      setPermissionState(initialPermissionState);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <StatusSwitch
            checked={hasPermission}
            onChange={(event) => {
              handleToggle(event.target.checked);
            }}
            disabled={isUpdating}
          />
          <StationLabel station={permissionState.station} />
        </Box>
      </CardContent>
    </Card>
  );
}
